// Décodage des e-mails dans le navigateur (voir docs/plan-technique.md, § 2) et préparation
// d'un HTML sûr à afficher dans une <iframe sandbox>.
import DOMPurify from "dompurify";
import PostalMime from "postal-mime";
import { recipientDetails, type RecipientDetails } from "./recipients";
import { inspectSecurityHtml } from "./security-html";
import type { SecurityAttachment, SecuritySource } from "./security-analysis";
import { extractMailSource, type MailSource } from "./mail-source";

export interface Attachment {
  filename: string;
  mimeType: string;
  size: number;
  url: string; // URL blob: locale, valable tant que la page est ouverte
  blob: Blob;  // contenu, lu sans requête (l'assistant IA l'analyse)
}

export interface Decoded {
  html: string | null; // déjà nettoyé
  text: string;
  attachments: Attachment[];
  hasRemoteImages: boolean;
  recipients: RecipientDetails;
  security: SecuritySource;
  original: MailSource;
  // Pour répondre : rattachement à la conversation et adresse de réponse demandée.
  messageId: string | null;
  references: string | null;
  replyTo: { name: string | null; address: string }[];
}

const cache = new Map<string, Decoded>();

// Nettoyeur propre aux e-mails reçus (ses réglages ne touchent pas celui de l'éditeur).
// Chaque lien s'ouvre sans garder de lien vers la messagerie : sans « noopener », la page
// ouverte pourrait remplacer l'e-mail affiché par une fausse page (« reverse tabnabbing »).
const mailPurifier = DOMPurify(window);
mailPurifier.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" || node.tagName === "AREA") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

// Types qu'un navigateur exécuterait s'il les ouvrait : la pièce jointe est alors seulement
// téléchargeable, jamais affichée dans l'origine de la messagerie.
const ACTIVE_TYPES = /html|xml|svg|javascript|ecmascript/i;

export function toBase64(buffer: ArrayBuffer): string {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

// Texte brut à partir du HTML, pour l'index de recherche quand l'e-mail n'a pas de partie texte.
function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("style, script, head").forEach((n) => n.remove());
  return (doc.body?.textContent ?? "").replace(/\s+/g, " ").trim();
}

export async function decode(id: string, raw: ArrayBuffer): Promise<Decoded> {
  const hit = cache.get(id);
  if (hit) return hit;
  const email = await PostalMime.parse(raw);

  // Images intégrées (cid:) : remplacées par des données locales, jamais chargées à distance.
  const inline = new Map<string, string>();
  const attachments: Attachment[] = [];
  const securityAttachments: SecurityAttachment[] = [];
  for (const a of email.attachments) {
    // Copie dans un tableau d'octets ordinaire, quel que soit le type rendu par postal-mime.
    const content = new Uint8Array(typeof a.content === "string" ? new TextEncoder().encode(a.content) : new Uint8Array(a.content));
    securityAttachments.push({ filename: a.filename || "pièce jointe", mimeType: a.mimeType || "application/octet-stream", size: content.byteLength, prefix: [...content.subarray(0, 16)] });
    const cid = a.contentId?.replace(/^<|>$/g, "");
    if (cid && a.mimeType.startsWith("image/")) inline.set(cid, `data:${a.mimeType};base64,${toBase64(content.buffer)}`);
    if (a.disposition !== "inline" || !cid) {
      const type = !a.mimeType || ACTIVE_TYPES.test(a.mimeType) ? "application/octet-stream" : a.mimeType;
      const blob = new Blob([content], { type });
      attachments.push({
        filename: a.filename || "pièce jointe",
        mimeType: a.mimeType || "application/octet-stream",
        size: blob.size,
        url: URL.createObjectURL(blob),
        blob,
      });
    }
  }

  let html: string | null = null;
  let hasRemoteImages = false;
  if (email.html) {
    const withInline = email.html.replace(/cid:([^"'\s)>]+)/gi, (whole, cid) => inline.get(cid) ?? whole);
    html = mailPurifier.sanitize(withInline, {
      WHOLE_DOCUMENT: true,
      FORBID_TAGS: ["form", "input", "button", "textarea", "select", "base", "meta", "link"],
      FORBID_ATTR: ["srcset"],
    });
    hasRemoteImages = /<img[^>]+src=["']?https?:/i.test(html) || /url\(\s*["']?https?:/i.test(html);
  }

  const text = email.text ?? (email.html ? htmlToText(email.html) : "");
  const decoded: Decoded = {
    html,
    text,
    attachments,
    hasRemoteImages,
    recipients: recipientDetails(email),
    original: await extractMailSource(raw),
    security: { headers: email.headers, from: email.from, replyTo: email.replyTo, returnPath: email.returnPath,
      // Le texte synthétisé depuis le HTML peut coller deux libellés de liens :
      // seuls les href et la vraie partie texte servent à examiner des destinations.
      date: email.date, messageId: email.messageId, references: email.references, text: email.text ?? "",
      html: inspectSecurityHtml(email.html ?? ""), attachments: securityAttachments },
    messageId: email.messageId ?? null,
    references: email.references ?? null,
    replyTo: (email.replyTo ?? []).filter((a) => a.address).map((a) => ({ name: a.name || null, address: a.address!.toLowerCase() })),
  };
  cache.set(id, decoded);
  return decoded;
}

// Document complet pour l'iframe : une politique de sécurité propre à l'e-mail bloque toute
// ressource distante, sauf les images si Robin l'a autorisé pour cet e-mail.
// Les e-mails HTML sont toujours affichés sur fond clair, même en thème sombre : beaucoup
// fixent la couleur du texte sans fixer celle du fond, et deviendraient illisibles.
export function frameDocument(html: string, allowRemoteImages: boolean): string {
  const csp = [
    "default-src 'none'",
    `img-src data: blob:${allowRemoteImages ? " https:" : ""}`,
    "style-src 'unsafe-inline'",
    "font-src data:",
  ].join("; ");
  const base = `<meta http-equiv="Content-Security-Policy" content="${csp}">
<base target="_blank">
<style>
  html { color-scheme: light; }
  body { margin: 0; padding: 16px; font: 15px/1.5 system-ui, sans-serif; overflow-wrap: anywhere; color: #1c1c1a; background: #fff; }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; }
  pre { white-space: pre-wrap; }
</style>`;
  return /<head[^>]*>/i.test(html) ? html.replace(/<head[^>]*>/i, (m) => m + base) : `<!doctype html><html><head>${base}</head><body>${html}</body></html>`;
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} Mo`;
}
